import { useCallback, useEffect, useRef, useState } from 'react';
import { devicesService, type LightOptions } from '../../../services/devices';
import type { Board } from '../../../types/device';
import { BoardBluetoothLink, bluetoothAvailable, chooserDismissed } from '../../../utils/board-bluetooth';

/**
 * Who lights the wall. `device` is the box on it, reached over MQTT — it works
 * for everybody standing there and asks nothing of the phone. `local` is this
 * browser holding its own Bluetooth link, which is the answer for a board with
 * no box on it. Never both: the server decides which one a board gets
 * (Board#lightable? and Board#locally_lightable?), and it never says yes twice.
 */
export type LightingTransport = 'device' | 'local';

export interface BoardLighting {
  /** How this board can be lit here, or null when it cannot be. */
  transport: LightingTransport | null;
  /** Whether the local link is open — meaningless on the device transport. */
  connected: boolean;
  /** What the board the browser connected to calls itself. */
  boardName: string | null;
  /**
   * Puts a problem on the wall. False means nothing was asked of it: no
   * transport, or the climber closed the Bluetooth chooser without picking a
   * board. A real failure throws, for the caller to report.
   *
   * Call it straight out of the click: opening the chooser needs the user
   * gesture, and it is lost behind an `await`.
   */
  light: (climbId: string, options?: LightOptions) => Promise<boolean>;
}

/**
 * One way to light a board, whichever way this board is lit — the lightbulb on
 * the climb profile and the circuit player share it, so a circuit walks the same
 * link the button opened rather than asking for the board a second time.
 *
 * The local transport is the browser doing the device's job: the server encodes
 * the command exactly as it would for a box on the wall, and the page writes it
 * over Web Bluetooth (see board-bluetooth.ts). Everything the protocol knows
 * stays on the server either way.
 *
 * Which is also why `signedIn` is asked for: both transports go through the
 * light endpoint, and that endpoint wants a session — the lighting is counted
 * against whoever asked for it. A visitor reading a public gym profile is shown
 * no button rather than one that could only come back with a 401.
 */
export function useBoardLighting(
  gymSlug: string | undefined,
  board: Board | null,
  signedIn: boolean
): BoardLighting {
  const [available, setAvailable] = useState(false);
  const [connected, setConnected] = useState(false);
  const [boardName, setBoardName] = useState<string | null>(null);
  const linkRef = useRef<BoardBluetoothLink | null>(null);

  const boardId = board?.id ?? null;
  // A board a device drives is lit by the device, so there is nothing to pair.
  const local = signedIn && !!board?.can_light_locally && !board?.can_light;

  // Whether this browser could do it at all: Chrome and Edge have Web Bluetooth,
  // Safari and Firefox have none, and a machine with its radio off has no board
  // to offer. Asked only where the answer could change what is shown.
  useEffect(() => {
    if (!local) {
      setAvailable(false);
      return;
    }

    let cancelled = false;
    void bluetoothAvailable().then((ok) => {
      if (!cancelled) setAvailable(ok);
    });

    return () => {
      cancelled = true;
    };
  }, [local]);

  // The board is given back when the page leaves it — a wall nobody lets go of
  // is unreachable for the next climber, and their own app cannot connect to it
  // either.
  useEffect(
    () => () => {
      linkRef.current?.disconnect();
      linkRef.current = null;
      setConnected(false);
      setBoardName(null);
    },
    [boardId]
  );

  const transport: LightingTransport | null =
    signedIn && board?.can_light ? 'device' : local && available ? 'local' : null;

  const light = useCallback(
    async (climbId: string, options: LightOptions = {}): Promise<boolean> => {
      if (!gymSlug || !board || !transport) return false;

      if (transport === 'device') {
        await devicesService.lightBoardClimb(gymSlug, board.id, climbId, options);
        return true;
      }

      let link = linkRef.current;
      if (!link) {
        link = new BoardBluetoothLink();
        // The board went, or another phone took it. Nothing to clean up — the
        // next command opens the chooser again.
        link.onLost(() => setConnected(false));
        linkRef.current = link;
      }

      if (!link.connected) {
        try {
          const name = await link.connect();
          setConnected(true);
          setBoardName(name);
        } catch (error) {
          if (chooserDismissed(error)) return false;
          throw error;
        }
      }

      const command = await devicesService.lightBoardClimb(gymSlug, board.id, climbId, {
        ...options,
        transport: 'local',
      });
      if (!command) return false;

      await link.show(command);
      return true;
    },
    [board, gymSlug, transport]
  );

  return { transport, connected, boardName, light };
}
